import { createUuid } from '@core/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AuthApi, UnauthApi } from '@/api';
import { DEFAULT_LOCATION, GEOLOCATION_VIEW_TYPE } from '@/constants/geo';
import { useAuthStore } from '@/stores/AuthStore';
import { useLocationConsent } from './useLocationConsent';
const LOCATION_QUERY_KEY = createUuid();
interface UseLocationOptions {
  /** 초기 데이터 가져오기 활성화 여부 */
  enabledInitialFetch?: boolean;
  geolocationViewType?: GEOLOCATION_VIEW_TYPE;
}
export const useLocation = ({
  enabledInitialFetch = false,
  geolocationViewType
}: UseLocationOptions = {}) => {
  const isLogin = useAuthStore(state => state.isLogin);
  const {
    isConsented,
    ensureConsent
  } = useLocationConsent();
  const reverseGeocoding = (coords: string, persistToServer: boolean) => persistToServer ? AuthApi['get:/wello/v2/auth/wello-policy-code/reverse-geocoding']({
    query: {
      coords,
      geolocationViewType: geolocationViewType ?? GEOLOCATION_VIEW_TYPE.WELLO_CLIENT
    }
  }) : UnauthApi['get:/wello/v2/allows/wello-policy-code/reverse-geocoding']({
    query: {
      coords
    }
  });
  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, isLogin, isConsented, geolocationViewType],
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const agreed = await ensureConsent();
      if (!agreed) return DEFAULT_LOCATION;

      // 로그인 + 동의 상태일 때만 auth 엔드포인트 호출 → 서버에 위치 저장
      const persistToServer_0 = isLogin === true;
      const location = await new Promise<typeof DEFAULT_LOCATION | undefined>(resolve => {
        navigator.geolocation.getCurrentPosition(async ({
          coords: coords_0
        }) => {
          try {
            const {
              data
            } = await reverseGeocoding(`${coords_0.longitude},${coords_0.latitude}`, persistToServer_0);
            const context = data?.context;

            // 해외 좌표 등 지역 정보 확인 불가 시 기본 지역으로 폴백
            if (!context?.region || !context.codeRegion || !context.subRegion || !context.codeSubRegion) {
              resolve(undefined);
              return;
            }
            resolve({
              longitude: coords_0.longitude,
              latitude: coords_0.latitude,
              region: context.region,
              codeRegion: context.codeRegion,
              subRegion: context.subRegion,
              codeSubRegion: context.codeSubRegion
            });
          } catch {
            resolve(undefined);
          }
        },
        // GPS 거부·실패 시 기본 지역으로 폴백 (NCP IP geolocation 종료, WEL-5240)
        () => resolve(undefined), {
          enableHighAccuracy: true,
          timeout: 5_000,
          maximumAge: 0
        });
      });
      return location ?? DEFAULT_LOCATION;
    },
    enabled: isConsented !== undefined && enabledInitialFetch
  });
};
export type LocationData = typeof DEFAULT_LOCATION;